// src/components/shared/StaffGroupSelect.jsx
//
// The Vehicles page's group control (VehiclesPage.jsx "Group filter"),
// lifted for STAFF groups: "All groups" / each group / "+ Create group"
// (swaps to an inline name input), and a delete button while a group is
// selected. Reads the shared staffGroups slice; writes go through the API,
// are dispatched, and then invalidate the slice (items kept) so every mounted list refetches
// authoritative member counts. A write that resolves after the active org
// changed (or after logout) dispatches nothing — it belongs to another org.
import { useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Trash2, X } from 'lucide-react';
import { staffGroupsActions } from '@/store';
import { staffGroups as staffGroupsApi } from '@/lib/trackizy';
import useStaffGroups from '@/hooks/useStaffGroups';
import { useConfirmDialog } from '@/components/shared/ConfirmDialog';
import { showToast } from '@/components/shared/Toast';

const SELECT_CLASS = {
  full: 'h-9 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/10',
  compact: 'w-full text-[11px] rounded-xl px-3 py-1.5 text-slate-600 outline-none bg-white/50 border border-white/60 focus:bg-white/70',
};

export default function StaffGroupSelect({ value, onChange, compact = false }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const store = useStore();
  const confirm = useConfirmDialog();
  const role = useSelector((s) => s.org?.role);
  const isSultan = useSelector((s) => s.sultan?.isSultan);
  const canManage = isSultan || role === 'admin' || role === 'manager';
  const { groups } = useStaffGroups({ enabled: canManage });
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');

  const selected = value !== 'all' ? groups.find((g) => g.id === value) : null;
  const currentOrgId = () => store.getState().org?.id ?? null;

  async function create(e) {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    const orgAtRequest = currentOrgId();
    try {
      const res = await staffGroupsApi.create({ name });
      if (currentOrgId() !== orgAtRequest) return;
      const group = res?.group ?? res;
      dispatch(staffGroupsActions.update(group));
      dispatch(staffGroupsActions.invalidate());
      setNewName('');
      setShowNew(false);
      onChange(group.id);
      showToast(t('staff.groupSaved', 'Staff group saved'));
    } catch (err) {
      // ApiError.message carries the server's `{ error }` body verbatim.
      showToast(err?.message === 'name_taken' || err?.code === 'name_taken'
        ? t('staff.groupNameTaken', 'A staff group with that name already exists')
        : `${t('common.failedSave')}: ${err.message}`);
    }
  }

  async function remove() {
    if (!selected) return;
    const orgAtRequest = currentOrgId();
    if (!await confirm({ title: t('common.confirmDelete', { name: selected.name }), variant: 'destructive' })) return;
    try {
      await staffGroupsApi.remove(selected.id);
      if (currentOrgId() !== orgAtRequest) return;
      dispatch(staffGroupsActions.remove(selected.id));
      dispatch(staffGroupsActions.invalidate());
      onChange('all');
      showToast(t('staff.groupDeleted', 'Staff group deleted'));
    } catch (err) {
      showToast(`${t('common.failedDelete')}: ${err.message}`);
    }
  }

  return (
    <div className={compact ? 'space-y-1.5' : 'flex items-center gap-1.5'}>
      <select
        value={value}
        onChange={(e) => {
          if (e.target.value === '__new__') { setShowNew(true); e.target.value = value; }
          else onChange(e.target.value);
        }}
        className={compact ? SELECT_CLASS.compact : SELECT_CLASS.full}
      >
        <option value="all">{t('staff.allGroups', 'All groups')}</option>
        {groups.map((g) => (
          <option key={g.id} value={g.id}>{`${g.name} (${g.member_count ?? 0})`}</option>
        ))}
        {canManage && <option value="__new__">+ {t('staff.createGroup', 'Create group')}</option>}
      </select>

      {canManage && selected && (
        <button
          type="button"
          onClick={remove}
          className="p-2 rounded-xl text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all"
          title={t('common.delete')}
        >
          <Trash2 size={14} />
        </button>
      )}

      {showNew && (
        <form className="flex items-center gap-1.5" onSubmit={create}>
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={t('staff.groupNamePlaceholder', 'Group name')}
            maxLength={80}
            className="h-9 w-36 px-3 text-xs rounded-xl border border-blue-300 dark:border-blue-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 outline-none focus:ring-2 focus:ring-blue-500/20"
          />
          <button type="submit" disabled={!newName.trim()} className="h-9 px-3 text-xs font-semibold rounded-xl text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-40">
            {t('settings.create')}
          </button>
          <button type="button" onClick={() => { setShowNew(false); setNewName(''); }} className="h-9 px-2 text-xs text-slate-400 hover:text-slate-600">
            <X size={14} />
          </button>
        </form>
      )}
    </div>
  );
}
